import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import {
  FaAward,
  FaChevronLeft,
  FaChevronRight,
  FaImages,
  FaTrash,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import Swal from "sweetalert2";

const ITEMS_PER_PAGE =10;

const ListGallery = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [gallery, setGallery] = useState([]);

  const indexOfLast = currentPage * ITEMS_PER_PAGE;
  const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
  const currentPlans = gallery.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(gallery.length / ITEMS_PER_PAGE);

  const renderPageNumbers = () => {
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return pages.map((page, i) =>
      page === "..." ? (
        <span key={i} className="px-2 text-gray-500 select-none">
          ...
        </span>
      ) : (
        <button
          key={i}
          onClick={() => setCurrentPage(page)}
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {page}
        </button>
      )
    );
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_gallery`
        );
        const data = await response.json();
        setGallery(data.data || []);
      } catch (error) {
        console.error("Error fetching Gallery:", error);
      }
    };

    fetchTeamMembers();
  }, []);

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/delete_gallery/${id}`,
          {
            method: "DELETE",
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          const updatedList = gallery.filter((member) => member._id !== id);
          setGallery(updatedList);

          const totalPagesAfterDelete = Math.ceil(
            updatedList.length / ITEMS_PER_PAGE
          );
          if (currentPage > totalPagesAfterDelete) {
            setCurrentPage(totalPagesAfterDelete); 
          }

          Swal.fire({
            title: "Deleted!",
            text: "Gallery has been deleted.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: data.message || "Failed to delete Gallery.",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error!",
          text: "Server error while deleting.",
          icon: "error",
        });
      }
    }
  };

  return (
    <>
      <div className="w-full bg-white">
        <div className="p-4 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">
            Show Gallery Documents
          </h2>

          <div className="overflow-x-auto rounded-xl shadow-lg">
            <motion.table
              className="min-w-full bg-white text-center border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <thead className="bg-blue-500 text-sm text-white font-semibold">
                <tr>
                  <th className="px-4 py-3 border">Sr No</th>
                  <th className="px-4 py-3 border">Image</th>
                  <th className="px-4 py-3 border">Title</th>
                  <th className="px-4 py-3 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPlans.length > 0 ? (
                  currentPlans.map((item, index) => (
                    <motion.tr
                      key={item.id}
                      className="hover:bg-gray-50 transition duration-300"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <td className="px-4 py-3">
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td className="py-3 px-4">
                        <button onClick={() => setSelectedImage(item.photo)}>
                          <img
                            src={item.photo}
                            alt={item.title}
                            className="w-12 h-12 rounded-full object-cover transition-transform duration-200 hover:scale-110 ring-2 ring-gray-300"
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-700">
                        {item.title}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="text-red-600 hover:text-red-800 transition-transform hover:scale-110"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-6 text-gray-500">
                      <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
                        <FaImages className="text-5xl text-gray-300 mb-4" />
                        <p className="text-xl text-gray-500 font-medium">
                          No gallery images available.
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          Once images are uploaded, they’ll show up here.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </motion.table>
          </div>

          {/* Image Enlarged View */}
          <AnimatePresence>
            {selectedImage && (
              <motion.div
                className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="relative"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                >
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute -top-4 -right-4 bg-white p-1 rounded-full shadow hover:bg-gray-200"
                  >
                    <IoMdClose className="text-black text-xl" />
                  </button>
                  <img
                    src={selectedImage}
                    alt="Enlarged"
                    className="max-w-[90vw] max-h-[80vh] rounded-xl shadow-2xl border-4 border-white"
                  />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination Controls */}
          {gallery.length >10 && (
            <div className="flex justify-center items-center mt-6 gap-2 flex-wrap">
              <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                <FaChevronLeft size={14} />
              </button>
              {renderPageNumbers()}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListGallery;
